import { useMemo } from "react";
import { Box, TextField, Checkbox, FormControlLabel, Typography } from "@material-ui/core";

import SearchIcon from "@mui/icons-material/Search";
import StoreIcon from "@mui/icons-material/StoreOutlined";
import LayersIcon from "@mui/icons-material/LayersOutlined";
import TagIcon from "@mui/icons-material/SellOutlined";
import WorkIcon from "@mui/icons-material/WorkOutline";
import CollectionsIcon from "@mui/icons-material/BallotOutlined";
import FilterIcon from "@mui/icons-material/FilterAltOutlined";
import ShowChart from "@mui/icons-material/ShowChart";

export default function Sidebar({
  currentBrand,
  families,
  selectedCategories,
  selectedSubcategories,
  selectedCollections,
  selectedCommercialNames,
  selectedStatuses,
  selectedVitrines,
  categorySearchTerm,
  setCategorySearchTerm,
  subcategorySearchTerm,
  setSubcategorySearchTerm,
  commercialSearchTerm,
  setCommercialSearchTerm,
  collectionSearchTerm,
  setCollectionSearchTerm,
  productSearchTerm,
  setProductSearchTerm,
  onlyStock,
  setOnlyStock,
  toggleFilter,
  theme,
  vitrinesMeta = {},
}) {
  const brandFamilies = families[currentBrand] || {};
  const keys = Object.keys(brandFamilies);

  const categories = useMemo(() => {
    const cats = new Set();
    keys.forEach((k) => cats.add(brandFamilies[k].category));
    return Array.from(cats).sort();
  }, [brandFamilies, keys]);

  const subcategories = useMemo(() => {
    const subs = new Set();
    keys.forEach((k) => subs.add(brandFamilies[k].subcategory));
    return Array.from(subs).sort();
  }, [brandFamilies, keys]);

  const commercialNames = useMemo(() => {
    const comms = new Set();
    keys.forEach((k) => comms.add(brandFamilies[k].commercialName));
    return Array.from(comms).sort();
  }, [brandFamilies, keys]);

  const vitrines = useMemo(() => {
    const vits = new Set();
    keys.forEach((k) => {
      (brandFamilies[k].products || []).forEach((p) => {
        if (p.vitrine != null) vits.add(p.vitrine);
      });
    });
    return Array.from(vits).sort();
  }, [brandFamilies, keys]);

  const filteredCategories = useMemo(() => {
    return categories.filter((c) =>
      c.toLowerCase().includes(categorySearchTerm.toLowerCase())
    );
  }, [categories, categorySearchTerm]);

  const filteredSubcategories = useMemo(() => {
    return subcategories.filter((s) =>
      s.toLowerCase().includes(subcategorySearchTerm.toLowerCase())
    );
  }, [subcategories, subcategorySearchTerm]);

  const filteredCommercialNames = useMemo(() => {
    return commercialNames.filter((cn) =>
      cn.toLowerCase().includes(commercialSearchTerm.toLowerCase())
    );
  }, [commercialNames, commercialSearchTerm]);

  const filteredCollections = useMemo(() => {
    return keys.filter((k) =>
      brandFamilies[k].name.toLowerCase().includes(collectionSearchTerm.toLowerCase())
    );
  }, [keys, brandFamilies, collectionSearchTerm]);

  const statusOptions = ["A", "C", "E", "F", "L", "P", "X"];

  const handleInputFocus = (e) => {
    const inputRoot = e.target.closest('.MuiOutlinedInput-root');
    if (inputRoot) {
      const fieldset = inputRoot.querySelector('fieldset');
      if (fieldset) {
        fieldset.style.borderColor = theme.primary;
      }
    }
  };

  const handleInputBlur = (e) => {
    const inputRoot = e.target.closest('.MuiOutlinedInput-root');
    if (inputRoot) {
      const fieldset = inputRoot.querySelector('fieldset');
      if (fieldset) {
        fieldset.style.borderColor = '#e5e7eb';
      }
    }
  };

  const getCheckboxStyle = (checked) => ({
    color: theme.primary,
    '&.Mui-checked': {
      color: theme.primary,
    },
  });

  return (
    <Box className="sidebar-container">
      <Box className="sidebar-search">
        <Typography className="sidebar-section-title">Filtrar Pedido</Typography>
        <Box className="search-input-wrapper">
          <SearchIcon className="search-icon" />
            <TextField
              fullWidth
              placeholder="Buscar Ref. ou Produto"
              value={productSearchTerm}
              onChange={(e) => setProductSearchTerm(e.target.value)}
              className="search-input"
              variant="outlined"
              InputProps={{
                className: "search-input-inner",
                startAdornment: null,
              }}
              inputProps={{
                style: { paddingLeft: "36px" },
              }}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
            />
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={onlyStock}
              onChange={(e) => setOnlyStock(e.target.checked)}
              className="b2b-checkbox"
              style={{ color: theme.primary }}
            />
          }
          label="Apenas Pronta Entrega"
          className="stock-check-label"
        />
      </Box>

      <Box className="sidebar-filters">
        <Box className="filter-section">
          <Typography className="filter-section-title">
            <StoreIcon className="filter-icon" />
            Vitrine
          </Typography>
          <Box className="filter-options">
            {vitrines.map((v) => (
              <FormControlLabel
                key={v}
                control={
                  <Checkbox
                    checked={selectedVitrines.has(v)}
                    onChange={() => toggleFilter("vitrine", v)}
                    className="b2b-checkbox"
                    style={{ color: theme.primary }}
                  />
                }
                label={vitrinesMeta[v]?.nome ?? v}
                className="filter-option"
              />
            ))}
          </Box>
        </Box>

        <Box className="filter-section">
          <Typography className="filter-section-title">
            <ShowChart className="filter-icon" />
            Status
          </Typography>
          <Box className="status-filters">
            {statusOptions.map((s) => (
              <Box
                key={s}
                className={`status-badge status-${s} ${
                  selectedStatuses.has(s) ? "selected" : ""
                }`}
                onClick={() => toggleFilter("status", s)}
              >
                {s}
              </Box>
            ))}
          </Box>
        </Box>

        <Box className="filter-section">
          <Typography className="filter-section-title">
            <LayersIcon className="filter-icon" />
            Categoria
          </Typography>
          <Box className="search-input-wrapper-small">
            <FilterIcon className="search-icon" />
            <TextField
              fullWidth
              placeholder="Buscar categoria..."
              value={categorySearchTerm}
              onChange={(e) => setCategorySearchTerm(e.target.value)}
              className="search-input-small"
              variant="outlined"
              InputProps={{
                className: "search-input-inner",
              }}
              inputProps={{
                style: { paddingLeft: "28px", fontSize: "12px" },
              }}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
            />
          </Box>
          <Box className="filter-options scrollable">
            {filteredCategories.map((cat) => (
              <FormControlLabel
                key={cat}
                control={
                  <Checkbox
                    checked={selectedCategories.has(cat)}
                    onChange={() => toggleFilter("category", cat)}
                    className="b2b-checkbox"
                    style={{ color: theme.primary }}
                  />
                }
                label={cat}
                className="filter-option"
              />
            ))}
          </Box>
        </Box>

        <Box className="filter-section">
          <Typography className="filter-section-title">
            <TagIcon className="filter-icon" />
            Subcategoria
          </Typography>
          <Box className="search-input-wrapper-small">
            <FilterIcon className="search-icon" />
            <TextField
              fullWidth
              placeholder="Buscar subcategoria..."
              value={subcategorySearchTerm}
              onChange={(e) => setSubcategorySearchTerm(e.target.value)}
              className="search-input-small"
              variant="outlined"
              InputProps={{
                className: "search-input-inner",
              }}
              inputProps={{
                style: { paddingLeft: "28px", fontSize: "12px" },
              }}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
            />
          </Box>
          <Box className="filter-options scrollable">
            {filteredSubcategories.map((sub) => (
              <FormControlLabel
                key={sub}
                control={
                  <Checkbox
                    checked={selectedSubcategories.has(sub)}
                    onChange={() => toggleFilter("subcategory", sub)}
                    className="b2b-checkbox"
                    style={{ color: theme.primary }}
                  />
                }
                label={sub}
                className="filter-option"
              />
            ))}
          </Box>
        </Box>

        <Box className="filter-section">
          <Typography className="filter-section-title">
            <WorkIcon className="filter-icon" />
            Nome Comercial
          </Typography>
          <Box className="search-input-wrapper-small">
            <FilterIcon className="search-icon" />
            <TextField
              fullWidth
              placeholder="Buscar nome..."
              value={commercialSearchTerm}
              onChange={(e) => setCommercialSearchTerm(e.target.value)}
              className="search-input-small"
              variant="outlined"
              InputProps={{
                className: "search-input-inner",
              }}
              inputProps={{
                style: { paddingLeft: "28px", fontSize: "12px" },
              }}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
            />
          </Box>
          <Box className="filter-options scrollable">
            {filteredCommercialNames.map((cn) => (
              <FormControlLabel
                key={cn}
                control={
                  <Checkbox
                    checked={selectedCommercialNames.has(cn)}
                    onChange={() => toggleFilter("commercial", cn)}
                    className="b2b-checkbox"
                    style={{ color: theme.primary }}
                  />
                }
                label={cn}
                className="filter-option"
              />
            ))}
          </Box>
        </Box>

        <Box className="filter-section">
          <Typography className="filter-section-title">
            <CollectionsIcon className="filter-icon" />
            Coleções
          </Typography>
          <Box className="search-input-wrapper-small">
            <FilterIcon className="search-icon" />
            <TextField
              fullWidth
              placeholder="Buscar coleção..."
              value={collectionSearchTerm}
              onChange={(e) => setCollectionSearchTerm(e.target.value)}
              className="search-input-small"
              variant="outlined"
              InputProps={{
                className: "search-input-inner",
              }}
              inputProps={{
                style: { paddingLeft: "28px", fontSize: "12px" },
              }}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
            />
          </Box>
          <Box className="filter-options scrollable">
            {filteredCollections.length === 0 ? (
              <Typography className="no-results">
                Nenhuma coleção encontrada.
              </Typography>
            ) : (
              filteredCollections.map((key) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      checked={selectedCollections.has(key)}
                      onChange={() => toggleFilter("collection", key)}
                      className="b2b-checkbox"
                      style={{ color: theme.primary }}
                    />
                  }
                  label={brandFamilies[key].name.replace("Coleção ", "")}
                  className="filter-option"
                />
              ))
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
